<template>
  <div class="flex flex-column gap-3">
    <h3>Profile</h3>
    <upload-logo
      class="m-auto"
      :height="100"
      :width="100"
      :url="user.photo_url"
      path="profile_photo"
      @upload="value => updateUserLogo(value)"
    />
    <a-form-model
      layout="horizontal"
      ref="form"
      class="flex gap-2 text-left w-full"
      :model="form"
      :rules="rules"
      :label-col="{ span: 10 }"
      :wrapper-col="{ span: 14 }"
    >
      <a-form-model-item
        :colon="false"
        label-align="left"
        label="First Name"
        prop="first_name"
        :validate-status="invalidChar.first_name ? 'error' : ''"
        :help="getHelpText('first_name')"
      >
        <a-input
          v-model="form.first_name"
          placeholder="First Name"
          autocomplete="off"
          @input="handleNameInput('first_name', $event)"
        />
      </a-form-model-item>
      <a-form-model-item
        :colon="false"
        label-align="left"
        label="Last Name"
        prop="last_name"
        :validate-status="invalidChar.last_name ? 'error' : ''"
        :help="getHelpText('last_name')"
      >
        <a-input
          v-model="form.last_name"
          placeholder="Last Name"
          autocomplete="off"
          @input="handleNameInput('last_name', $event)"
        />
      </a-form-model-item>
      <a-form-model-item :colon="false" label-align="left" label="Email" prop="email">
        <a-input v-model="form.email" placeholder="Email" autocomplete="off" />
      </a-form-model-item>
      <a-form-model-item :colon="false" label-align="left" label="Phone" prop="phone">
        <phone-input
          @input="
            value => {
              $refs.form.validateField('phone')
              form.phone = value
            }
          "
          :value="form.phone"
        /> </a-form-model-item
      ><a-form-model-item
        :colon="false"
        label-align="left"
        :label="getTaxIdentifierLabel(form.is_ssn)"
        prop="social_security_number"
      >
        <tax-identifier-input
          v-model="form.social_security_number"
          :is-ssn.sync="form.is_ssn"
          @type-change="revalidateTaxIdentifier('social_security_number')"
        />
      </a-form-model-item>
      <a-form-model-item :colon="false" label-align="left" label="Birth Date" prop="birth_date">
        <a-date-picker
          v-model="form.birth_date"
          placeholder="Birth Date"
          class="w-full"
          :allowClear="true"
          :format="visualDateFormat"
        />
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="SMS" prop="is_allow_sms">
        <a-checkbox v-model="form.is_allow_sms">I would like to receive SMS messages </a-checkbox>
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="Local Time" prop="is_allow_sms">
        <a-checkbox v-model="form.use_device_time_zone">
          Time is set by your local time zone. When you change time zones, the app's time will update to match the new
          one. Great for people who travel often.
        </a-checkbox>
      </a-form-model-item>

      <a-form-model-item
        v-if="!form.use_device_time_zone"
        :colon="false"
        label-align="left"
        label="Time Zone"
        prop="timezone"
      >
        <custom-select
          :multiple="false"
          :value.sync="form.timezone"
          placeholder="Time Zone"
          :options="timeZones"
          name="id"
          optionKey="id"
        />
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="Active Payment Type">
        <custom-select
          :multiple="false"
          :value.sync="selectedPaymentType"
          placeholder="Payment Type"
          :options="paymentTypes"
          name="name"
          optionKey="id"
        />
      </a-form-model-item>

      <h4>Address (optional)</h4>
      <a-form-model-item :colon="false" label-align="left" label="Address Line 1" prop="address.address_line1">
        <a-input v-model="form.address.address_line1" placeholder="Enter Address" />
      </a-form-model-item>
      <a-form-model-item :colon="false" label-align="left" label="Address Line 2" prop="address.address_line2">
        <a-input v-model="form.address.address_line2" placeholder="Enter Address" />
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="City" prop="address.city">
        <a-input v-model="form.address.city" placeholder="Enter City" />
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="Country" prop="address.country">
        <custom-select
          :multiple="false"
          :value.sync="countryProxy"
          placeholder="Enter Country"
          :options="countriesList"
          name="name"
          optionKey="code"
        />
      </a-form-model-item>

      <a-form-model-item
        v-if="form.address.country === 'US' || form.address.country === 'us'"
        :colon="false"
        label-align="left"
        label="State"
        prop="address.state"
      >
        <custom-select
          :multiple="false"
          :value.sync="stateProxy"
          placeholder="Enter State"
          :options="statesList"
          name="name"
          optionKey="code"
        />
      </a-form-model-item>

      <a-form-model-item :colon="false" label-align="left" label="Zipcode" prop="address.zipcode">
        <a-input v-model="form.address.zipcode" placeholder="Enter Zipcode" />
      </a-form-model-item>

      <a-button @click="save" type="primary" class="ml-auto w-fit" html-type="submit" :loading="loading">
        Save
      </a-button>
    </a-form-model>

    <div class="flex flex-column gap-2">
      <h4>Your Default Signature</h4>
      <div class="flex flex-column gap-2">
        <signature v-if="adminAssociateSignature" :available-signature="adminAssociateSignature" />

        <span v-else>Default signature not found</span>

        <signature ref="signatureRef" v-if="creatingNewSignature" @save-signature="handleSaveNewSignature" />

        <div class="flex justify-content-between">
          <a-button v-if="!creatingNewSignature" type="primary" class="ml-auto" @click="creatingNewSignature = true">
            {{ adminAssociateSignature ? 'Change Signature' : 'Create Signature' }}
          </a-button>
          <template v-else>
            <a-button @click="creatingNewSignature = false"> Cancel </a-button>
            <a-button :loading="loadingSignature" type="primary" @click="saveNewSignature"> Save </a-button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Signature from '@/components/signature.vue'
import CustomSelect from '@/components/ui/custom-select.vue'
import PhoneInput from '@/components/ui/phone-input.vue'
import constData from '@/helpers/const-data'
import UploadLogo from '@/modules/project/components/upload-logo.vue'
import { deepCopy } from '@/utils/deep-copy'
import { Checkbox, DatePicker, FormModel, Input, notification } from 'ant-design-vue'
import moment from 'moment-timezone'
import { createNamespacedHelpers, mapActions } from 'vuex'
import TaxIdentifierInput from '@/components/ui/tax-identifier-input.vue'
import { createTaxIdentifierRule, getTaxIdentifierLabel, normalizeTaxIdentifier } from '@/utils/tax-identifier'

const { mapActions: userActions } = createNamespacedHelpers('user')

const initialForm = {
  first_name: undefined,
  last_name: undefined,
  email: undefined,
  phone: undefined,
  birth_date: undefined,
  address: {
    address_line1: undefined,
    address_line2: undefined,
    city: undefined,
    state: undefined,
    zipcode: undefined,
    country: undefined
  },
  social_security_number: undefined,
  is_ssn: true,
  is_allow_sms: false,
  use_device_time_zone: false,
  timezone: null
}

const initialRules = {
  first_name: [
    {
      required: true,
      message: 'First Name can not be empty',
      trigger: 'change'
    }
  ],
  last_name: [
    {
      required: true,
      message: 'Last Name can not be empty',
      trigger: 'change'
    }
  ],
  email: [
    {
      required: true,
      message: 'Email can not be empty',
      trigger: 'change',
      type: 'email'
    }
  ],
  birth_date: [
    {
      required: true,
      message: 'Birth date can not be empty',
      trigger: 'change'
    }
  ],
  phone: [
    {
      required: true,
      message: 'Phone can not be empty',
      trigger: 'change'
    },
    {
      transform: value => (value ? value.replace(/\s+/g, '') : value),
      pattern: /^\+\d{7,18}$/,
      message: 'Invalid phone number. It should start with a + and contain 10 to 15 digits.'
    }
  ],
  address: {
    zipcode: [
      {
        min: 5,
        message: 'The zipcode must be more than 4 characters.',
        trigger: 'change'
      }
    ]
  },
  social_security_number: [],
  timezone: [
    {
      required: true,
      message: 'Time zone can not be empty',
      trigger: 'change'
    }
  ]
}

export default {
  components: {
    'a-input': Input,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-date-picker': DatePicker,
    'a-checkbox': Checkbox,
    'phone-input': PhoneInput,
    'custom-select': CustomSelect,
    'upload-logo': UploadLogo,
    signature: Signature,
    'tax-identifier-input': TaxIdentifierInput
  },
  data() {
    return {
      selectedPaymentType: this.$store.state.applicationState.currentPaymentType ?? 'all',
      profile_photo_id: undefined,
      form: { ...initialForm },
      rules: {
        ...initialRules,
        first_name: [
          {
            required: true,
            message: 'Please enter your first name',
            trigger: 'blur'
          },
          { validator: this.associateNameValidator, trigger: 'blur' }
        ],
        last_name: [
          {
            required: true,
            message: 'Please enter your last name',
            trigger: 'blur'
          },
          { validator: this.associateNameValidator, trigger: 'blur' }
        ],
        social_security_number: [
          createTaxIdentifierRule(() => this.form.is_ssn)
        ]
      },

      countriesList: constData.countries,
      statesList: constData.usStates,

      loading: false,
      loadingSignature: false,

      creatingNewSignature: false,

      baseHelpText: 'Only letters, spaces, hyphens (-), and slashes (/) allowed.',
      invalidChar: {
        first_name: null,
        last_name: null
      },

      timeZones: moment.tz.names().map(item => ({ id: item }))
    }
  },
  computed: {
    paymentTypes() {
      return [{ name: 'All', id: 'all' }, ...this.$store.state.applicationState.paymentTypes]
    },
    user() {
      return this.$store.state.applicationState.user
    },
    adminAssociateSignature() {
      return this.$store.state.applicationState.associate.signature
        ? {
            ...this.$store.state.applicationState.associate.signature,
            signed_date: this.$store.state.applicationState.associate.signature.signature_date
          }
        : null
    },
    visualDateFormat() {
      return this.$store.state.applicationState.dateFormat
    },
    stateProxy: {
      get() {
        return this.form.address.state ? this.form.address.state.toUpperCase() : undefined
      },
      set(value) {
        this.form.address.state = value
      }
    },

    countryProxy: {
      get() {
        return this.form.address.country ? this.form.address.country.toUpperCase() : undefined
      },
      set(value) {
        this.form.address.country = value
      }
    }
  },
  watch: {
    selectedPaymentType: {
      handler(newVal) {
        if (newVal) {
          this.handleChangePaymentType(newVal)
        }
      }
    },
    '$store.state.applicationState.user': {
      handler(newVal) {
        if (newVal) {
          this.form = deepCopy(newVal)
          this.$set(this.form, 'is_ssn', this.form.is_ssn !== false)

          this.form.address = newVal.address
            ? deepCopy(newVal.address)
            : {
                ...initialForm.address
              }

          this.form.birth_date = newVal.birth_date ? moment(newVal.birth_date) : null
        }
      },
      immediate: true,
      deep: true
    },
    'form.address.country': {
      handler(newVal) {
        if (newVal && newVal.toUpperCase() !== 'US') {
          this.form.address.state = undefined
        }
      }
    },
    'form.use_device_time_zone': {
      handler() {
        this.form.timezone = null
      }
    }
  },
  methods: {
    ...userActions(['updateUser', 'updateLogo']),
    ...mapActions(['switchPaymentType', 'loadApplicationState', 'setLoading', 'updateSignature']),

    getTaxIdentifierLabel,

    revalidateTaxIdentifier(field) {
      this.$nextTick(() => {
        this.$refs.form.validateField(field)
      })
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    async saveNewSignature() {
      await this.$refs.signatureRef.saveSignature()
    },

    associateNameValidator(rule, value, callback) {
      if (!value) {
        return callback()
      }

      const allowedPattern = /^[a-zA-Z\s\-\/]+$/
      if (!allowedPattern.test(value)) {
        return callback(new Error('Only letters, spaces, hyphens (-), and forward slashes (/) are allowed.'))
      }

      if (value.trim() === '') {
        return callback(new Error('Name cannot be empty.'))
      }

      callback()
    },

    handleNameInput(field, e) {
      const rawValue = e.target.value

      const invalidMatch = rawValue.match(/[^a-zA-Z\s\-\/]/)
      const invalidSymbol = invalidMatch ? invalidMatch[0] : null

      this.invalidChar[field] = invalidSymbol

      const cleaned = rawValue.replace(/[^a-zA-Z\s\-\/]/g, '')

      const formatted = cleaned
        .toLowerCase()
        .split(' ')
        .map(word =>
          word
            .split(/([\-\/])/)
            .map(part => {
              if (part === '-' || part === '/') return part
              return part.charAt(0).toUpperCase() + part.slice(1)
            })
            .join('')
        )
        .join(' ')

      this.form[field] = formatted

      if (invalidSymbol) {
        setTimeout(() => {
          if (this.invalidChar[field] === invalidSymbol) {
            this.invalidChar[field] = null
          }
        }, 2000)
      }
    },

    getHelpText(field) {
      if (this.invalidChar[field]) {
        const char = this.invalidChar[field]
        const displayChar = char === ' ' ? 'space' : char === '\t' ? 'tab' : `"${char}"`
        return `Invalid character ${displayChar}. ${this.baseHelpText}`
      }
      return this.baseHelpText
    },

    async save() {
      this.$refs.form.validate(async valid => {
        if (!valid) {
          this.showNotification('warning', 'Validation Error', 'Please fill in all required fields correctly.')

          return
        }
        this.loading = true

        this.form.birth_date = moment(this.form.birth_date).utc().startOf('day').toISOString()
        this.form.email = this.form.email.toLowerCase()
        this.form.is_ssn = this.form.is_ssn !== false
        this.form.social_security_number = normalizeTaxIdentifier(this.form.social_security_number) || null

        const info = deepCopy(this.form)

        const payload = {
          user: {
            ...info,
            profile_photo_id: this.profile_photo_id,
            id: this.form.id
          }
        }

        try {
          await this.updateUser(payload)

          this.showNotification('success', 'Success', 'User has been successfully updated')

          this.loading = false

          this.setLoading(true)

          await this.loadApplicationState()
        } catch (error) {
          this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        } finally {
          this.loading = false
        }
      })
    },

    async updateUserLogo(file_id) {
      this.setLoading(true)

      try {
        await this.updateLogo({ id: this.user.id, file_id })
        this.showNotification('success', 'Success', "User's photo has been successfully updated")
        await this.loadApplicationState()
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      }
    },

    async handleSaveNewSignature(file_id) {
      try {
        this.loadingSignature = true

        await this.updateSignature({
          id: this.user.user_id,
          file_id
        })

        await this.loadApplicationState()

        this.showNotification('success', 'Success', 'New Signature Uploaded Successfully')

        this.creatingNewSignature = false
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        this.loadingSignature = false
      }
    },

    async handleChangePaymentType(payment_type_id) {
      this.setLoading(true)
      try {
        await this.switchPaymentType({
          payment_type_id: payment_type_id === 'all' ? null : payment_type_id
        })

        await this.loadApplicationState()
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      }
    }
  }
}
</script>

<style scoped>
.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  gap: 8px;
}

.ant-input-number {
  width: 100%;
}
</style>
